import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EngineeringIcon from "@mui/icons-material/Engineering";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { showSnackbar } from "../../features/snackbar/snackbarSlice";
import {
  Comment,
  createComment,
  deleteComment,
  getCommentsByOrder,
  getCommentsByTicket,
  readCommentsBulk,
  updateComment,
  uploadCommentAttachment,
  validateAttachments,
  MAX_ATTACHMENTS_PER_COMMENT,
} from "../../services/commentService";

export type CommentTargetTicket = {
  id: string;
  file_name: string;
};

type Props = {
  /** 案件ID（必須） */
  orderId: string;
  /**
   * 図面単位で表示する場合の ticket_id。
   * 指定時は当該図面のコメントのみ表示・投稿する（チケット詳細画面）。
   * 未指定時は案件 + 配下全図面のコメントを統合表示する（注文詳細画面）。
   */
  ticketId?: string | null;
  /** 案件配下の図面一覧（注文詳細画面での投稿先セレクタ用） */
  tickets?: CommentTargetTicket[];
};

const typeLabel: Record<string, string> = {
  customer: "顧客",
  employee: "社内",
  subcontractor: "外注先",
};

const typeColor: Record<string, string> = {
  customer: "#E3F2FD",
  employee: "#F1F8E9",
  subcontractor: "#FFF3E0",
};

const AuthorIcon: React.FC<{ type: string }> = ({ type }) => {
  const color =
    type === "customer"
      ? "#1976D2"
      : type === "employee"
      ? "#558B2F"
      : "#EF6C00";
  const Icon =
    type === "customer"
      ? PersonIcon
      : type === "employee"
      ? BusinessIcon
      : EngineeringIcon;
  return (
    <Avatar sx={{ bgcolor: color, width: 32, height: 32 }}>
      <Icon fontSize="small" />
    </Avatar>
  );
};

const CommentTimeline: React.FC<Props> = ({ orderId, ticketId, tickets }) => {
  const dispatch = useAppDispatch();
  const accountInfo = useAppSelector((state) => state.accountInfo);
  const isCustomer = accountInfo.type === "customer";
  const isEmployee = accountInfo.type === "employee";

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [body, setBody] = useState("");
  // 公開範囲を選べるのは自社のみ。事故防止のため初期値は「社内のみ」
  const [visibility, setVisibility] = useState<"public" | "internal">(
    "internal"
  );
  const [files, setFiles] = useState<File[]>([]);
  // 投稿先: "" = 案件全体 / それ以外 = 特定図面の ticket_id
  const [targetTicket, setTargetTicket] = useState<string>(ticketId || "");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 編集
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuCommentId, setMenuCommentId] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    if (!orderId && !ticketId) return;
    setLoading(true);
    try {
      const data = ticketId
        ? await getCommentsByTicket(ticketId)
        : await getCommentsByOrder(orderId);
      setComments(data);
      // 可視コメントを一括既読化（自分の投稿・既読済みを除く）
      const unreadIds = data
        .filter((c) => !c.is_read && !c.is_own)
        .map((c) => c.id);
      if (unreadIds.length) {
        try {
          await readCommentsBulk(unreadIds);
        } catch {
          /* 既読化失敗は無視 */
        }
      }
    } catch (e) {
      dispatch(
        showSnackbar({ message: "コメントの取得に失敗しました", type: "error" })
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, ticketId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const combined = [...files, ...selected];
    const err = validateAttachments(combined);
    if (err) {
      dispatch(showSnackbar({ message: err, type: "error" }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFiles(combined);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!body.trim()) {
      dispatch(showSnackbar({ message: "本文を入力してください", type: "error" }));
      return;
    }
    setSubmitting(true);
    try {
      const uploaded = [];
      for (const f of files) {
        uploaded.push(await uploadCommentAttachment(f));
      }
      const effectiveTicketId = ticketId || (targetTicket || null);
      await createComment({
        order_id: orderId,
        ticket_id: effectiveTicketId,
        body,
        // 顧客は常に public（お客様のみ）、協力会社は常に internal（社内のみ）
        visibility: isCustomer
          ? "public"
          : isEmployee
          ? visibility
          : "internal",
        attachments: uploaded,
      });
      setBody("");
      setFiles([]);
      setVisibility("internal");
      if (!ticketId) setTargetTicket("");
      await fetchComments();
      dispatch(showSnackbar({ message: "コメントを投稿しました", type: "success" }));
    } catch (e) {
      dispatch(
        showSnackbar({ message: "コメントの投稿に失敗しました", type: "error" })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, id: string) => {
    setMenuAnchor(e.currentTarget);
    setMenuCommentId(id);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuCommentId(null);
  };

  const startEdit = (c: Comment) => {
    setEditingId(c.id);
    setEditingBody(c.body);
    closeMenu();
  };

  const handleUpdate = async () => {
    if (!editingId || !editingBody.trim()) return;
    try {
      await updateComment(editingId, editingBody);
      setEditingId(null);
      setEditingBody("");
      await fetchComments();
      dispatch(showSnackbar({ message: "コメントを更新しました", type: "success" }));
    } catch {
      dispatch(
        showSnackbar({ message: "コメントの更新に失敗しました", type: "error" })
      );
    }
  };

  const handleDelete = async (id: string) => {
    closeMenu();
    try {
      await deleteComment(id);
      await fetchComments();
      dispatch(showSnackbar({ message: "コメントを削除しました", type: "success" }));
    } catch {
      dispatch(
        showSnackbar({ message: "コメントの削除に失敗しました", type: "error" })
      );
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        コメント
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : comments.length === 0 ? (
        <Typography sx={{ color: "#888", mb: 2 }}>
          コメントはまだありません。
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {comments.map((c) => (
            <Box
              key={c.id}
              sx={{
                bgcolor: typeColor[c.author_type] || "#F5F5F5",
                borderRadius: 2,
                p: 2,
                border: "1px solid #E0E0E0",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <AuthorIcon type={c.author_type} />
                <Box sx={{ flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {c.author_name}
                    </Typography>
                    <Chip
                      size="small"
                      label={typeLabel[c.author_type] || c.author_type}
                    />
                    {/* 両方の公開範囲が見えるのは自社のみなので、目印も自社にだけ出す */}
                    {isEmployee && c.visibility === "internal" && (
                      <Chip size="small" color="warning" label="社内のみ" />
                    )}
                    {isEmployee && c.visibility === "public" && (
                      <Chip size="small" color="info" label="お客様のみ" />
                    )}
                    {(c.kind === "rejection_reason" ||
                      c.kind === "order_rejection_reason") && (
                      <Chip size="small" color="error" label="差し戻し理由" />
                    )}
                    {/* 案件統合表示時のみ図面ラベルを表示 */}
                    {!ticketId && c.file_name && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`📄 ${c.file_name}`}
                      />
                    )}
                    <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>
                      {new Date(c.created_at).toLocaleString("ja-JP")}
                      {c.updated_at ? "（編集済み）" : ""}
                    </Typography>
                  </Stack>

                  {editingId === c.id ? (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={editingBody}
                        onChange={(e) => setEditingBody(e.target.value)}
                        size="small"
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleUpdate}
                          disabled={!editingBody.trim()}
                        >
                          保存
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingId(null);
                            setEditingBody("");
                          }}
                        >
                          キャンセル
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Typography
                      sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {c.body}
                    </Typography>
                  )}

                  {c.attachments && c.attachments.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      {c.attachments.map((a) => (
                        <Chip
                          key={a.id}
                          icon={<AttachFileIcon />}
                          label={a.file_name}
                          component="a"
                          href={a.url}
                          target="_blank"
                          clickable
                          size="small"
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                {c.is_own && editingId !== c.id && (
                  <IconButton size="small" onClick={(e) => openMenu(e, c.id)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            const c = comments.find((x) => x.id === menuCommentId);
            if (c) startEdit(c);
          }}
        >
          編集
        </MenuItem>
        <MenuItem
          onClick={() => menuCommentId && handleDelete(menuCommentId)}
          sx={{ color: "error.main" }}
        >
          削除
        </MenuItem>
      </Menu>

      {/* 投稿フォーム */}
      <Box sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          placeholder="コメントを入力..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          size="small"
        />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mt: 1.5 }}
        >
          {/* 投稿先セレクタ（注文詳細=統合表示時のみ） */}
          {!ticketId && tickets && tickets.length > 0 && (
            <TextField
              select
              size="small"
              label="投稿先"
              value={targetTicket}
              onChange={(e) => setTargetTicket(e.target.value)}
              sx={{ minWidth: 200 }}
              // 「案件全体」は空文字を値に使うため、displayEmpty と renderValue が
              // 無いと選択しても入力欄が空のままになり、選べないように見える。
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  if (!value) return "案件全体";
                  const selected = tickets?.find((t) => t.id === value);
                  return selected ? `📄 ${selected.file_name}` : "案件全体";
                },
              }}
            >
              <MenuItem value="">案件全体</MenuItem>
              {tickets.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  📄 {t.file_name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* 公開範囲トグル（自社のみ） */}
          {isEmployee && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={visibility}
              onChange={(_, v) => v && setVisibility(v)}
            >
              <ToggleButton value="public">お客様のみ</ToggleButton>
              <ToggleButton value="internal">社内のみ</ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* 添付 */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            component="label"
            disabled={files.length >= MAX_ATTACHMENTS_PER_COMMENT}
          >
            添付
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept=".dwg,.dxf,.pdf,.jpg,.jpeg,.png,.zip"
              onChange={handleSelectFiles}
            />
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={submitting || !body.trim()}
          >
            {submitting ? "投稿中..." : "投稿"}
          </Button>
        </Stack>

        {files.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
            {files.map((f, idx) => (
              <Chip
                key={`${f.name}-${idx}`}
                label={f.name}
                onDelete={() => removeFile(idx)}
                deleteIcon={<CloseIcon />}
                size="small"
              />
            ))}
          </Stack>
        )}
        <Typography sx={{ color: "#999", fontSize: "0.75rem", mt: 1 }}>
          添付は dwg/dxf/pdf/jpg/jpeg/png/zip、1ファイル50MBまで、1コメント5ファイルまで。
        </Typography>
      </Box>
    </Box>
  );
};

export default CommentTimeline;
